import React, { useEffect, useState } from 'react';
import axios from 'axios';
import styles from './Jobs.module.css';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from './firebase/firebase';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { getAuth } from "firebase/auth";

const getPaginationPages = (currentPage, totalPages, siblingCount = 1) => {
  const totalPageNumbers = siblingCount * 2 + 5;

  if (totalPages <= totalPageNumbers) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const leftSiblingIndex = Math.max(currentPage - siblingCount, 2);
  const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages - 1);

  const showLeftEllipsis = leftSiblingIndex > 2;
  const showRightEllipsis = rightSiblingIndex < totalPages - 1;

  const pages = [];

  pages.push(1);

  if (showLeftEllipsis) {
    pages.push('left-ellipsis');
  } else {
    for (let i = 2; i < leftSiblingIndex; i++) {
      pages.push(i);
    }
  }

  for (let i = leftSiblingIndex; i <= rightSiblingIndex; i++) {
    pages.push(i);
  }

  if (showRightEllipsis) {
    pages.push('right-ellipsis');
  } else {
    for (let i = rightSiblingIndex + 1; i < totalPages; i++) {
      pages.push(i);
    }
  }

  pages.push(totalPages);

  return pages;
};

const Jobs = () => {
  const [user, loading] = useAuthState(auth);
  const [jobs, setJobs] = useState([]);
  const [categories, setCategories] = useState([]); // ✅ store categories
  const [filters, setFilters] = useState({
    date: '',
    category: '', // ✅ new
    location: '',
    title: '',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
const locationHook = useLocation();
const [filtersReady, setFiltersReady] = useState(false); // NEW

// ✅ Read query params from URL and update filters on first load
useEffect(() => {
  const params = new URLSearchParams(locationHook.search);
  
  setFilters({
    title: params.get('title') || '',
    category: params.get('category') || '',
    location: params.get('location') || '',
    date: params.get('date') || ''
  });

  setCurrentPage(1);
   setFiltersReady(true); // ✅ Mark filters loaded
}, [locationHook.search]);

  const jobsPerPage = 10;

useEffect(() => {
  const fetchCategories = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/jobs/categories`
      );
      setCategories(res.data || []);
    } catch (err) {
      console.error("Error fetching categories", err);
    }
  };

  fetchCategories();
}, []);



  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

useEffect(() => {
  if (!filtersReady || !user) return;

  const fetchJobs = async () => {
    try {
      const token = await getAuth().currentUser.getIdToken();

      const params = {
        page: currentPage,
        title: filters.title,
        category: filters.category,
        location: filters.location,
        date: filters.date,
      };

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/jobs`,
        {
          params,
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setJobs(res?.data?.jobs || []);
      setTotalPages(res?.data?.pages || 1);
    } catch (err) {
      console.error("Error fetching jobs:", err);
    }
  };

  fetchJobs();
}, [filtersReady, filters, currentPage, user]);


  if (loading) return <p>Loading...</p>;

  if (!user) {
    return (
      <div className={styles.restrictedMessage}>
        <h3>🔒 Access Restricted</h3>
        <p>
          <strong>Please </strong>
          <Link to="/login" className={styles.authLink}>
            Login
          </Link>
          <strong> or </strong>
          <Link to="/register" className={styles.authLink}>
            Register
          </Link>
          <strong> to view all job opportunities.</strong>
        </p>
      </div>
    );
  }

  const onPageChange = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      setCurrentPage(page);
      scrollToTop();
    }
  };

  const paginationPages = getPaginationPages(currentPage, totalPages);

  return (
    <div className={styles.jobListContainer}>
      <div style={{ textAlign: 'center' }}>
        <div className={styles.jobHeaderButton}>Available Jobs</div>
      </div>

     <div className={styles.filterNotice}>
  <div className={styles.filterNoticeHeading}>🔍 Looking for a job?</div>
  <div className={styles.filterNoticeSubtext}>
    Filter by Date, Job Categories, Location
  </div>
</div>


      {/* Filter Section */}
      <div className={styles.filterBar}>
        <div className={styles.filterField}>
          <label>📅 Date</label>
          <input
            type="date"
            value={filters.date}
            onChange={(e) => {
              setFilters({ ...filters, date: e.target.value });
              setCurrentPage(1);
            }}
          />
        </div>

       {/* ✅ New Category Dropdown */}
  <div className={styles.filterField}>
    <label>📂 Category</label>
    <select
      value={filters.category}
      onChange={(e) => {
        setFilters({ ...filters, category: e.target.value });
        setCurrentPage(1);
      }}
    >
      <option value="">All Categories</option>
      {categories.map((cat, index) => (
        <option key={index} value={cat._id}>
          {cat._id} ({cat.count})
        </option>
      ))}
    </select>
  </div>

        <div className={styles.filterField}>
          <label>📍 Location</label>
          <input
            placeholder="Location"
            value={filters.location}
            onChange={(e) => {
              setFilters({ ...filters, location: e.target.value });
              setCurrentPage(1);
            }}
          />
        </div>

      
      </div>

      {/* Pagination Top */}
      <div style={{ textAlign: 'center', margin: '30px 0 10px' }}>
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={styles.paginationButton}
        >
          Prev
        </button>

        {paginationPages.map((page, idx) => {
          if (page === 'left-ellipsis' || page === 'right-ellipsis') {
            return (
              <span key={idx} className={styles.paginationEllipsis}>
                ...
              </span>
            );
          }
          return (
            <button
              key={idx}
              onClick={() => onPageChange(page)}
              className={`${styles.paginationButton} ${
                currentPage === page ? styles.activePage : ''
              }`}
            >
              {page}
            </button>
          );
        })}

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={styles.paginationButton}
        >
          Next
        </button>
      </div>

      {/* Go to Page Dropdown (Same Line) */}
     <div className={styles.pageSelectContainer}>
  <span className={styles.goToPageBox}>Go to Page:</span>
  <select
    id="page-select"
    value={currentPage}
    onChange={(e) => onPageChange(Number(e.target.value))}
    className={styles.pageSelect}
  >
    {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
      <option key={pageNum} value={pageNum}>
        {pageNum}
      </option>
    ))}
  </select>
</div>


      {/* Job Cards */}
 {jobs.length === 0 ? (
  <p className={styles.noJobs}>No matching jobs found.</p>
) : (
  jobs.map((job, index) => (
    <div className={styles.jobCard} key={job._id}>

      {/* Job Category */}
    
{Array.isArray(job.categories) && job.categories.length > 0 && (
  <div className={styles.categoryList}>
    {job.categories.map((cat, index) => (
      <span key={index} className={styles.categoryBadge}>
        {cat}
      </span>
    ))}
  </div>
)}


  <h3 className={styles.jobTitle}>
    {(currentPage - 1) * jobsPerPage + index + 1}.{" "}
    <Link to={`/jobs/${job._id}`} className={styles.jobTitleLink}>
      {job.jobHeading || job.title}
    </Link>
  </h3>

  <div className={styles.postedDate}>
    📅 Posted on: {new Date(job.createdAt).toLocaleDateString("en-GB")}
  </div>

  <Link to={`/jobs/${job._id}`} className={styles.readMoreButton}>
    Read More →
  </Link>

  <div className={styles.jobDisclaimer}>
    <strong>Disclaimer:</strong> Please verify all details before applying.{" "}
    <Link to="/disclaimer">Read full disclaimer</Link>
  </div>
</div>
  ))
)}


      {/* Pagination Bottom */}
      <div style={{ textAlign: 'center', margin: '30px 0' }}>
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={styles.paginationButton}
        >
          Prev
        </button>

        {paginationPages.map((page, idx) => {
          if (page === 'left-ellipsis' || page === 'right-ellipsis') {
            return (
              <span key={idx} className={styles.paginationEllipsis}>
                ...
              </span>
            );
          }
          return (
            <button
              key={idx}
              onClick={() => onPageChange(page)}
              className={`${styles.paginationButton} ${
                currentPage === page ? styles.activePage : ''
              }`}
            >
              {page}
            </button>
          );
        })}

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={styles.paginationButton}
        >
          Next
        </button>
      </div>

      {/* Scroll to Top */}
      <div className={styles.goToTopFixed} style={{ textAlign: 'center', marginTop: '20px' }}>
        <button onClick={scrollToTop} style={{ padding: '8px 16px', borderRadius: '4px' }}>
          ⬆ Top
        </button>
      </div>
    </div>
  );
};

export default Jobs;
